import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '@lecomptoir/shared';
import { useCurrentUser } from '@/lib/auth';

/**
 * Garde de route : restreint l'accès aux utilisateurs ayant l'un des rôles
 * indiqués. Redirige vers /connexion si non authentifié, vers / si rôle
 * insuffisant.
 *
 * Mode observateur (super-admin) :
 *   Quand un super-admin navigue en mode observateur, `/auth/me` retourne le
 *   rôle de l'utilisateur cible (ex. 'student') ET le champ `impersonatedBy`.
 *   RequireRole doit laisser passer ces requêtes même si le rôle retourné
 *   diffère de celui attendu normalement pour la route — le backend a déjà
 *   validé le token HMAC et garanti que c'est le bon utilisateur.
 */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { data: user, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="container py-16 text-center text-muted-foreground" role="status">
        Chargement…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/connexion" state={{ from: location.pathname }} replace />;
  }

  // Mode observateur : si impersonatedBy est présent, le role du cache est
  // déjà celui de l'utilisateur cible — on laisse passer sans vérification
  // de liste supplémentaire (le backend est la source de vérité).
  if (!roles.includes(user.role) && !user.impersonatedBy) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
